import React, { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../services/api";
import { SurveyContext } from "../context/SurveyContext";
import { AuthContext } from "../context/AuthContext";
import { SocketContext } from "../context/SocketContext";
import { FaUserPlus, FaSpinner, FaCheck } from "react-icons/fa";
import { getProfilePicture, AVATAR_OPTIONS } from "../utils/avatar";

const Register = () => {
  const {
    instanceId,
    conditionId,
    isInstanceReady,
    participantId,
    caseNumber,
  } = useContext(SurveyContext);
  const { setAuthFromToken } = useContext(AuthContext);
  const { authenticateSocket } = useContext(SocketContext);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [bio, setBio] = useState("");
  const [avatarSeed, setAvatarSeed] = useState(
    () => AVATAR_OPTIONS[Math.floor(Math.random() * AVATAR_OPTIONS.length)].seed
  );
  const [error, setError] = useState("");
  const [userExists, setUserExists] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setUserExists(false);
    setIsSubmitting(true);

    try {
      if (!instanceId) {
        setError("No session found. Please start from the beginning.");
        return;
      }

      const registrationData = {
        username,
        password,
        bio,
        avatarSeed,
        instanceId,
        surveyToken: participantId,
        caseNumber,
      };

      console.log("[Register] Sending registration data:", {
        ...registrationData,
        password: `[${password.length} chars]`,
      });

      const response = await api.post("/auth/register", registrationData);

      // Auto-login after successful registration
      if (response.data.token) {
        await setAuthFromToken(response.data.token, {
          userId: response.data.userId,
          username: response.data.username,
          instanceId: response.data.instanceId,
          condition: response.data.condition,
          caseNumber: response.data.caseNumber,
        });

        // Notify socket that this is an active human user registration
        // This triggers orchestrator to activate agent behavior
        // Pass token explicitly since React state may not have updated yet
        if (authenticateSocket) {
          authenticateSocket(response.data.token);
        }

        // Navigate to newsfeed - InstanceLoader will show OnboardingFlow if not completed
        navigate("/newsfeed");
      } else {
        navigate("/login");
      }
    } catch (error) {
      console.error("[Register] Error registering user:", error.response?.data || error);
      if (error.response && error.response.data) {
        if (error.response.data.error === "Username already exists in this instance") {
          setUserExists(true);
        } else if (error.response.data.errors) {
          // Show validation errors with field names
          const errorMessages = error.response.data.errors
            .map((err) => `${err.path || err.param}: ${err.msg}`)
            .join(", ");
          setError(errorMessages);
        } else if (error.response.data.error) {
          setError(error.response.data.error);
        }
      } else {
        setError("An unexpected error occurred");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if instance is ready
  if (!instanceId || !isInstanceReady) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-spiders-bg">
        <div className="spiders-card p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-spiders-primary-950 flex items-center justify-center">
            <FaSpinner className="w-8 h-8 text-spiders-primary-400 animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-spiders-text-primary mb-4">Session Not Ready</h1>
          <p className="text-spiders-text-secondary mb-6">
            Your session is still being prepared. Please wait...
          </p>
          <Link to="/" className="text-spiders-primary-400 hover:text-spiders-primary-300 transition-colors">
            Go back to start
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-screen bg-spiders-bg py-8">
      <div className="spiders-card p-8 w-full max-w-md relative overflow-hidden">
        {/* Decorative gradient */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-spiders-accent-400 via-spiders-primary-500 to-spiders-accent-400"></div>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-spiders-accent-950 flex items-center justify-center">
            <FaUserPlus className="w-5 h-5 text-spiders-accent-400" />
          </div>
          <h1 className="text-2xl font-bold text-spiders-text-primary">Create Your Account</h1>
        </div>
        <p className="text-spiders-text-secondary text-sm mb-6 ml-13">
          Join the discussion and share your thoughts
        </p>

        {error && (
          <div className="bg-spiders-error/20 border border-spiders-error text-spiders-error p-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {userExists && (
          <div className="bg-spiders-warning/20 border border-spiders-warning text-spiders-warning p-3 rounded-lg mb-4 text-sm">
            This username is already taken.
            <Link to="/login" className="underline ml-1 hover:text-spiders-warning">
              Login instead?
            </Link>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-spiders-text-primary text-sm font-medium mb-2" htmlFor="username">
              Username
            </label>
            <input
              type="text"
              id="username"
              className="spiders-input w-full"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Choose a username"
              required
              minLength={3}
            />
            <p className="text-spiders-text-muted text-xs mt-1">
              This will be visible to other users
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-spiders-text-primary text-sm font-medium mb-2" htmlFor="password">
              Password
            </label>
            <input
              type="password"
              id="password"
              className="spiders-input w-full"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
              required
              minLength={6}
            />
            <p className="text-spiders-text-muted text-xs mt-1">
              At least 6 characters
            </p>
          </div>

          <div className="mb-5">
            <label className="block text-spiders-text-primary text-sm font-medium mb-2" htmlFor="bio">
              Bio <span className="text-spiders-text-muted">(optional)</span>
            </label>
            <textarea
              id="bio"
              className="spiders-input w-full resize-none"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell others a bit about yourself..."
              rows={3}
            />
          </div>

          {/* Avatar picker */}
          <div className="mb-6">
            <label className="block text-spiders-text-primary text-sm font-medium mb-2">
              Choose your avatar
            </label>
            <div className="grid grid-cols-8 gap-2">
              {AVATAR_OPTIONS.map((opt) => (
                <button
                  key={opt.seed}
                  type="button"
                  onClick={() => setAvatarSeed(opt.seed)}
                  className={`relative rounded-full p-0.5 transition-all duration-150 ${
                    avatarSeed === opt.seed
                      ? "ring-2 ring-spiders-primary-400 ring-offset-2 ring-offset-spiders-bg-surface scale-110"
                      : "hover:ring-2 hover:ring-spiders-border hover:ring-offset-1 hover:ring-offset-spiders-bg-surface opacity-70 hover:opacity-100"
                  }`}
                >
                  <img
                    src={getProfilePicture(null, opt.seed)}
                    alt={opt.label}
                    className="w-9 h-9 rounded-full"
                  />
                  {avatarSeed === opt.seed && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-spiders-primary-400 rounded-full flex items-center justify-center">
                      <FaCheck className="w-2 h-2 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
              isSubmitting
                ? "bg-spiders-bg-elevated text-spiders-text-muted cursor-not-allowed"
                : "spiders-btn-accent"
            }`}
          >
            {isSubmitting ? (
              <>
                <FaSpinner className="animate-spin" />
                Creating Account...
              </>
            ) : (
              "Create Account"
            )}
          </button>
        </form>

        <p className="mt-6 text-spiders-text-secondary text-center text-sm">
          Already have an account?
          <Link to="/login" className="text-spiders-primary-400 hover:text-spiders-primary-300 ml-1 transition-colors">
            Login here
          </Link>
        </p>

        {/* Session Info — only shown in test mode (or in dev outside a real study run) */}
        {process.env.NODE_ENV === "development" && (!window.__studyMode || window.__studyTestMode) && (
          <div className="mt-6 p-3 bg-spiders-bg-dark rounded-lg text-xs text-spiders-text-muted border border-spiders-border">
            <p>Instance: {instanceId || "(empty)"}</p>
            <p>Condition: {conditionId || "(empty)"}</p>
            <p>isInstanceReady: {isInstanceReady ? "true" : "false"}</p>
            {participantId && <p>Participant: {participantId}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;
